import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import analyticsDe from "@/features/analytics/i18n/de.json";
import analyticsEn from "@/features/analytics/i18n/en.json";
import analyticsPl from "@/features/analytics/i18n/pl.json";
import comparisonDe from "@/features/comparison/i18n/de.json";
import comparisonEn from "@/features/comparison/i18n/en.json";
import comparisonPl from "@/features/comparison/i18n/pl.json";
import jobsDe from "@/features/jobs/i18n/de.json";
import jobsEn from "@/features/jobs/i18n/en.json";
import jobsPl from "@/features/jobs/i18n/pl.json";
import landingDe from "@/features/landing/i18n/de.json";
import landingEn from "@/features/landing/i18n/en.json";
import landingPl from "@/features/landing/i18n/pl.json";
import legalDe from "@/features/legal/i18n/de.json";
import legalEn from "@/features/legal/i18n/en.json";
import legalPl from "@/features/legal/i18n/pl.json";
import ordersDe from "@/features/orders/i18n/de.json";
import ordersEn from "@/features/orders/i18n/en.json";
import ordersPl from "@/features/orders/i18n/pl.json";
import uploadsDe from "@/features/uploads/i18n/de.json";
import uploadsEn from "@/features/uploads/i18n/en.json";
import uploadsPl from "@/features/uploads/i18n/pl.json";
import viewerDe from "@/features/viewer/i18n/de.json";
import viewerEn from "@/features/viewer/i18n/en.json";
import viewerPl from "@/features/viewer/i18n/pl.json";
import commonDe from "@/locales/common.de.json";
import commonEn from "@/locales/common.en.json";
import commonPl from "@/locales/common.pl.json";

export const SUPPORTED_LANGUAGES = ["pl", "en", "de"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const i18n = i18next.createInstance();

// Polish is the default for EVERY visitor: the storefront ships PL-only for now,
// and the language switcher (footer) is a forward-looking opt-in, not an
// auto-negotiation. So we deliberately do NOT detect `navigator.language` — an
// `en`/`de` browser still lands on PL. The only thing that overrides PL is an
// EXPLICIT prior choice the visitor made via the switcher, persisted in
// localStorage and re-applied by `applyStoredLanguage` in a post-hydration effect.
//
// Under the build-time prerender (ADR-0053 §1) the landing `/` is rendered to
// static HTML on the server, then hydrated on the client. The first client
// render MUST match that HTML byte-for-byte, so the initial language is FIXED to
// PL — the live locale and the prerender's canonical locale (ADR-0053 §3) — on
// both sides. Applying a stored choice is deferred to `applyStoredLanguage` so the
// first render still matches the baked PL HTML; the switch is then a normal client
// update, never a hydration mismatch. The detector is registered client-only so
// `i18n.init` never touches `localStorage`/`document` during prerender.
const isBrowser = typeof window !== "undefined";
if (isBrowser) i18n.use(LanguageDetector);

i18n.use(initReactI18next).init({
  lng: "pl",
  fallbackLng: "pl",
  supportedLngs: SUPPORTED_LANGUAGES,
  defaultNS: "common",
  ns: [
    "common",
    "jobs",
    "viewer",
    "uploads",
    "landing",
    "comparison",
    "orders",
    "legal",
    "analytics",
  ],
  resources: {
    pl: {
      common: commonPl,
      jobs: jobsPl,
      viewer: viewerPl,
      uploads: uploadsPl,
      landing: landingPl,
      comparison: comparisonPl,
      orders: ordersPl,
      legal: legalPl,
      analytics: analyticsPl,
    },
    en: {
      common: commonEn,
      jobs: jobsEn,
      viewer: viewerEn,
      uploads: uploadsEn,
      landing: landingEn,
      comparison: comparisonEn,
      orders: ordersEn,
      legal: legalEn,
      analytics: analyticsEn,
    },
    de: {
      common: commonDe,
      jobs: jobsDe,
      viewer: viewerDe,
      uploads: uploadsDe,
      landing: landingDe,
      comparison: comparisonDe,
      orders: ordersDe,
      legal: legalDe,
      analytics: analyticsDe,
    },
  },
  interpolation: { escapeValue: false },
  // localStorage ONLY — never `navigator`. A fresh visitor has no stored value,
  // so detection returns nothing and the app stays on the PL default. Switching
  // via the footer writes the choice here (`caches`), so it persists across
  // reloads and navigation for that browser.
  detection: {
    order: ["localStorage"],
    caches: ["localStorage"],
  },
});

/**
 * Re-apply the visitor's EXPLICIT prior language choice (persisted in
 * localStorage by the footer switcher), switching away from the PL default the
 * app hydrated with (see the `lng: "pl"` note above). MUST be called only AFTER
 * hydration — from a client effect — so the first render still matches the
 * prerendered PL HTML; the `changeLanguage` it triggers is then an ordinary
 * post-hydration update. A no-op on the server, for a first-time visitor (no
 * stored choice → PL), and when the stored language already matches the current.
 */
export function applyStoredLanguage(): void {
  if (!isBrowser) return;
  const detected = i18n.services.languageDetector?.detect();
  const candidate = Array.isArray(detected) ? detected[0] : detected;
  const normalized = candidate?.split("-")[0];
  if (
    normalized &&
    normalized !== i18n.resolvedLanguage &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(normalized)
  ) {
    void i18n.changeLanguage(normalized);
  }
}
